/**
 * `GameSessionProvider` / `useGameSessionContext` — the **single** Game-room session
 * for the whole app.
 *
 * **The session must be a singleton**: every `useGameSession()` call opens its own
 * `/ws/game` socket and holds its own slot lock, so two calls in one browser tab race
 * two reclaim sockets for the same slot. This provider owns the one `useGameSession`
 * instance (with the browser {@link useGameEffects effect context} wired in so GM
 * directives drive the real router/overlay), and every consumer — the root gate, the
 * login card, the gated pages — reads it from context.
 *
 * Mount it **inside** the router (it uses `useRouter` via `useGameEffects`) but around
 * the gate + `<Outlet/>`. The {@link DirectiveOverlay} is rendered here too, once, so
 * a GM banner/FX shows on every route rather than only on the lobby. A GM's overlay shows the
 * banner standing on the players' screens (`session.playerBanner`), so the GM sees what the
 * table sees; the transmission itself persists on the player slots only.
 */
import { createContext, useContext, type ReactNode } from "react";
import { useGameSession, type GameSession } from "./useGameSession";
import { DirectiveOverlay, useGameEffects } from "./useGameEffects";
import { DiceOverlay } from "../dice/DiceOverlay";
import { DiceFeedSubscriber } from "../dice/DiceFeedSubscriber";
import { KissSoundSubscriber } from "../audio/KissSoundSubscriber";

// Exported (not just the provider/hook below) so a component-render test can feed a fake
// `GameSession` straight into `<GameSessionContext.Provider>` without standing up the real
// hook/socket, as `CompendiumContext` is exported for the same reason.
export const GameSessionContext = createContext<GameSession | null>(null);

/** Provide the one app-wide Game session (effects wired) + the directive overlay. */
export function GameSessionProvider({ children }: { children: ReactNode }) {
  const { effects, overlay } = useGameEffects();
  const session = useGameSession({ effects });
  return (
    <GameSessionContext.Provider value={session}>
      <DirectiveOverlay state={session.role === "gm" ? { ...overlay, banner: session.playerBanner } : overlay} />
      {/* The global 3D dice surface — a peer of the directive overlay, above every route. */}
      <DiceOverlay />
      {/* Tumbles a new ad-hoc `/roll` from the authoritative feed on the overlay above. A peer of
          the overlay (not in the window manager); remove it and the feed/COMMS.app are unaffected. */}
      <DiceFeedSubscriber feed={session.feed} feedBaselineTs={session.feedBaselineTs} />
      {/* Sounds a NEW `/kiss` emote on every client — a peer of the dice subscriber above, same
          reason: it must watch the feed regardless of which window (if any) is open, since
          COMMS.app's window state doesn't survive a reload. */}
      <KissSoundSubscriber feed={session.feed} feedBaselineTs={session.feedBaselineTs} />
      {children}
    </GameSessionContext.Provider>
  );
}

/**
 * Read the app-wide Game session. Throws if used outside {@link GameSessionProvider} —
 * every Game-session consumer goes through the one provider (no stray second socket),
 * so a missing provider is a wiring bug, not a runtime fallback.
 */
export function useGameSessionContext(): GameSession {
  const session = useContext(GameSessionContext);
  if (session === null) {
    throw new Error("useGameSessionContext must be used within a <GameSessionProvider>");
  }
  return session;
}
